// ============================================================
// ELC Shift — Uploads / Rota module (v103.0)
//
// AI rota generation + publish pipeline. The central, production-
// critical path: build a context (per-store rules + trading drives +
// staff availability), call Claude to generate the schedule, then
// publish to DB with audit trails for shift + delivery changes.
//
// This is where v97 found 3 silent bugs from v96 typeof leftovers
// (generateRotaMain L5461 / buildTradingContext). The typeof-guard
// audit step in the playbook was banked because of this code path —
// quiet failures here cost real pilot days.
//
// Public surface:
//   • generateRotaMain (async, 328 LOC) — orchestrates the AI call
//     and renders the resulting rota to the UI. Calls into:
//       Uploads.rules.resolveRulesForStore (v96 bug 1 fix)
//       Uploads.trading.buildContext       (v96 bug 2 fix)
//
//   • publishRota (async, 75 LOC) — persist the current rota to DB.
//     Writes audit_log entries via auditShifts + auditDeliveries.
//
//   • auditShifts / auditDeliveries — internal helpers for the audit
//     trail. Co-located here because they're publishRota-only.
//
// Free globals consumed:
//   DB, showToast, ALL_STORES, staffData, weekStart, currentRota,
//   localDateStr, businessDayStr (Dubai UTC+4 + 3am-3am boundary),
//   escapeHtml, formatDateShort
// ============================================================

import { generatorContext, needAtFn, coverWarnings } from '../worker/cover-check.js';
import { weekendFor, peakDaysFor } from '../shared/rota-country.js';   // v133.94: Qatar's Friday-Saturday weekend
import { withTimeout, startTicker, AI_CALL_LIMIT_MS, AI_NOTES_LIMIT_MS, AI_SKIP_REFINE_SECS, TOO_SLOW } from '../shared/rota-wait.js';   // v133.94: no silent freeze
import { dedupeShiftRows } from '../shared/shift-dedupe.js';   // v136.12: one row per person and day in a publish

// v132.100 - COVER CHECK (docs/cover-check-plan.md, Stage 4). The store's own
// hours, what each day needs, and its real targets BY DATE (this generator
// otherwise uses sample targets for day-off costs). Null when the country is
// not set up or anything fails: the generator then runs exactly as before.
export async function loadCoverContext(store, week, isQatar) {
  try {
    const base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
    const r = await fetch(base + '/api/cover-check/config', { cache: 'no-store' });
    const config = r.ok ? await r.json() : null;
    const country = isQatar ? 'Qatar' : 'UAE';
    if (!config || !config.configured || !config.configured[country]) return null;
    const plus = (s, n) => { const d = new Date(s + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
    const dates = [0, 1, 2, 3, 4, 5, 6].map((i) => plus(week, i));
    const [hoursRows, kpiRows] = await Promise.all([
      DB.openingHours.list().catch(() => []),
      DB.kpi.list({ store, from: plus(week, -63), to: plus(week, 6) }).catch(() => []),
    ]);
    const rows = Array.isArray(kpiRows) ? kpiRows : (kpiRows && Array.isArray(kpiRows.rows) ? kpiRows.rows : []);
    const targetsByDate = {};
    rows.forEach((x) => { if (x && x.date) targetsByDate[String(x.date).slice(0, 10)] = x.target; });
    return generatorContext({ store, country, dates, config, hoursRows: Array.isArray(hoursRows) ? hoursRows : [], targetsByDate, history: rows });
  } catch (e) {
    console.warn('[AI rota] cover check unavailable - generating as before:', e && e.message);
    return null;
  }
}

export async function generateRotaMain() {
  const store = document.getElementById('aiStoreMain').value;
  const week = document.getElementById('aiWeekMain').value;
  const ramadan = document.getElementById('aiRamadanMain').value === 'yes';
  const notes = document.getElementById('aiNotesMain').value;
  if (!store || !week) { showToast('Select a store and week','error'); return; }

  const btn = document.getElementById('aiMainBtn');
  const status = document.getElementById('aiStatusMain');
  const result = document.getElementById('aiMainResult');
  btn.disabled = true;
  const DOTS = '<span class="ai-dot"></span><span class="ai-dot"></span><span class="ai-dot"></span>';
  const ticker = startTicker(status, DOTS, 'Claude is building the rota...');   // v133.94: counts up, never a silent wait
  result.style.display = 'none';

  try {
    const storeStaff = staffData.filter(s => s.store === store && s.active !== false);
    if (!storeStaff.length) throw new Error('No active staff for ' + store);

    // Trading pattern — cascaded per store
    const tp = Uploads.rules.resolveRulesForStore(store);  // v97.0: removed typeof guard that silently fell through to loadTradingPattern (no cascade)
    tp._storeName = store;
    const tradingCtx = Uploads.trading.buildContext(tp);  // v97.0: was bare typeof guard

    // v133.94: the STORE's country (as the staff app does), not the first person's: a blank country on one staff
    // record made a Qatar store a UAE one
    const country = (typeof getStoreCountry === 'function' ? getStoreCountry(store) : null) || storeStaff[0].country || 'ELC - UAE';
    const isQatar = country === 'ELC - Qatar';

    // Shift templates — admin-editable via trading pattern
    const templates = getShiftTemplates(tp);

    // Day lists — normalise to short-form
    const noDoWeekendLong = weekendFor(isQatar, tp);   // v133.94: Qatar Friday and Saturday (was a Thursday-Friday default)
    const allowedDOLong   = (tp.allowedDaysOff && tp.allowedDaysOff.length) ? tp.allowedDaysOff : ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    const peakDaysLong    = peakDaysFor(isQatar, tp);   // v133.94: Qatar Thursday to Saturday (Sunday is a working day there)
    const noDoWeekend = noDoWeekendLong.map(toShortDay).filter(Boolean);
    const allowedDO   = allowedDOLong.map(toShortDay).filter(Boolean);
    const peakDays    = peakDaysLong.map(toShortDay).filter(Boolean);

    // Delivery data for this store — build deliveryDayStarts keyed by short day
    const storeDeliveries = deliveryData.filter(d => d.store === store);
    const deliveryDayStarts = {};
    storeDeliveries.forEach(d => {
      const dayShort = toShortDay(d.day);
      if (!dayShort || !d.window) return;
      const start = String(d.window).split('-')[0].trim();
      if (timeToMin(start) === null) return;
      if (!deliveryDayStarts[dayShort] || timeToMin(start) < timeToMin(deliveryDayStarts[dayShort])) {
        deliveryDayStarts[dayShort] = start;
      }
    });
    // v133.98: who is away which days this week (booked leave, work at another store), from the saved rota.
    // Fail-soft: no read, nobody marked away (the generator runs as it did, and publish still keeps leave).
    // Cover staff stay out of the admin generator: its publish writes no work_store, so their shifts would
    // land at their own store. The staff app plans them, on their cover days only.
    const weekOrder = isQatar ? ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'] : ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
    let away = {};
    try {
      const homeIds = {};
      storeStaff.forEach(s => { homeIds[s.id] = true; });
      const weekRows = await DB.shifts.list({ week });
      away = ROTA.awayFromCells((Array.isArray(weekRows) ? weekRows : [])
        .filter(r => r && homeIds[r.staff_id])
        .map(r => ({ staffId: r.staff_id, day: r.day, type: r.type, workStore: r.work_store, oosStore: r.work_store })), homeIds, store);
    } catch (e) { console.warn('[AI rota] could not read this week\'s leave - nobody marked away:', e && e.message); away = {}; }
    // ── STRUCTURED INTAKE (v131.5, R2 - see the staff app for the story) ──
    // FAIL-SOFT: any error and generation proceeds as before, with the notes
    // riding in the prompt as free text.
    const firstNamesEarly = storeStaff.map(s => s.name.split(' ')[0]);
    let requestsByStaffId = {};
    let intakeApplied = null;
    let mergedDeliveryStarts = deliveryDayStarts;
    if (notes && notes.trim()) {
      try {
        ticker.setLabel('Reading your notes...');
        const iRaw = await withTimeout(DB.ai.rota({ purpose: 'rota-intake', model: 'claude-haiku-4-5-20251001', max_tokens: 1000,
          system: 'Output ONLY raw JSON. First character must be {, last must be }.',
          messages: [{ role: 'user', content: buildIntakePrompt(notes, firstNamesEarly, weekOrder) }] }), AI_NOTES_LIMIT_MS, 'reading the notes took too long');
        ticker.setLabel('Claude is building the rota...');
        // one parser everywhere (v131.8)
        const iParsed = parseRotaResponse(iRaw);
        if (!iParsed) throw new Error('intake returned no JSON');
        intakeApplied = applyIntake(sanitiseIntake(iParsed, firstNamesEarly), deliveryDayStarts, storeStaff);
        mergedDeliveryStarts = intakeApplied.deliveryDayStarts;
        requestsByStaffId = intakeApplied.requestsByStaffId;
        away = ROTA.mergeAway(away, intakeApplied.awayByStaffId);   // v133.98
      } catch (e) { console.warn('[AI rota] intake parse failed - continuing with free text:', e && e.message); intakeApplied = null; ticker.setLabel('Claude is building the rota...'); }
    }
    const deliveryDaysShort = Object.keys(mergedDeliveryStarts);

    // Targets — admin uses TARGETS_SAMPLE as the fallback (per-day shape already short-form)
    const targets = {};
    DAYS.forEach(d => { targets[d] = (TARGETS_SAMPLE[d] && TARGETS_SAMPLE[d].target) || 0; });

    // Deterministic D/O allocation
    // ── SHIFT HISTORY (v131.6, R3 - see the staff app) ── FAIL-SOFT.
    let historySummary = null;
    const recentDoByStaffId = {};
    try {
      const histRows = [];
      const staffIdSet = {};
      storeStaff.forEach(s => { staffIdSet[s.id] = true; });
      const weekMs = new Date(week + 'T12:00:00').getTime();
      for (let hw = 1; hw <= 3; hw++) {
        const d = new Date(weekMs - hw * 7 * 86400000);
        const wkPrev = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
        const prevRows = await DB.shifts.list({ week: wkPrev });
        (Array.isArray(prevRows) ? prevRows : []).forEach(r => {
          if (r && staffIdSet[r.staff_id] && (r.seq || 1) < 2) histRows.push(r);
        });
      }
      historySummary = summariseShiftHistory(histRows, storeStaff, templates);
      storeStaff.forEach(s => {
        const rec = historySummary.byStaffId[s.id];
        if (rec && rec.doDays.length) recentDoByStaffId[s.id] = rec.doDays[0];
      });
    } catch (e) { console.warn('[AI rota] history load failed - generating history-blind:', e && e.message); historySummary = null; }
    // v132.100 cover check: real hours, needs, and days off on the store's own
    // three quietest days (never its two busiest). Typed requests still win.
    const cover = await loadCoverContext(store, week, isQatar);
    const coverHours = cover ? cover.hoursByDay : null;
    const doAllowed = (cover && cover.quietDays) ? cover.quietDays : allowedDO;
    const doForbidden = (cover && cover.busyDays) ? cover.busyDays : noDoWeekend;
    const doTargets = (cover && cover.quietDays) ? cover.targetsByDay : targets;
    const doByStaffId = allocateDaysOff(storeStaff, doTargets, doAllowed, doForbidden, deliveryDaysShort, requestsByStaffId, recentDoByStaffId, away);
    const doEntries = storeStaff.map(s => {
      const fn = s.name.split(' ')[0];
      const day = doByStaffId[s.id];
      return day ? { staffName: fn, day: day, type: 'off' } : null;
    }).filter(Boolean);

    // Pending requests for this store (admin only)
    // Leave requests: only those that touch the week being written (r.leave is the server's record).
    const _weekEnd = (() => { const d = new Date(String(week) + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + 6); return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10); })();
    const pendingReqs = (typeof requestsData !== 'undefined' ? requestsData : [])
      .filter(r => r.store === store && r.status === 'pending')
      .filter(r => !r.leave || !_weekEnd || (r.leave.start_date <= _weekEnd && r.leave.end_date >= String(week)));

    // v131.9 (R4c) — the skeleton lives in rota.js (buildRotaPromptCore), ONE
    // builder for both apps. The local copy that used to be here had already
    // drifted from the staff app's. Admin-specific facts travel as DATA:
    const extraContext = [];
    if (pendingReqs.length) extraContext.push('Pending requests:');
    // Leave requests (docs/leave-requests-plan.md): requestsData now holds the server's requests (LeaveRequestsAdmin),
    // so these are real pending leave requests. Not on the rota until approved; the note is never sent (reason '').
    pendingReqs.forEach(r => extraContext.push('- ' + r.staffName + ': ' + r.type + ' on ' + r.date + (r.reason ? ' - "' + r.reason + '"' : '')));
    if (storeDeliveries.length) extraContext.push('Deliveries: ' + storeDeliveries.map(d => d.day + ' ' + d.window + (d.notes ? ' (' + d.notes + ')' : '')).join('; '));
    if (tradingCtx) extraContext.push(tradingCtx);
    const prompt = buildRotaPromptCore({
      store: store,
      weekLabel: 'week starting ' + week,
      staff: storeStaff, firstNames: firstNamesEarly, doByStaffId: doByStaffId,
      templates: templates, morningSplit: tp.morningSplit || 50, peakDays: peakDays,
      deliveryDayStarts: mergedDeliveryStarts,
      awayByStaffId: away,                             // v133.98
      extraContext: extraContext,
      managerNotes: notes,
      intakeRules: formatIntakeRules(intakeApplied),
      historyLines: formatHistoryLines(historySummary, storeStaff),
      ramadanHours: ramadan ? 6 : null,
      hoursByDay: coverHours,                          // v132.100 cover check
      coverLines: cover ? cover.coverLines : null,     // v132.100 cover check
    });

    const firstNames = storeStaff.map(s => s.name.split(' ')[0]);
    const hasManagerNotes = !!(notes && notes.trim().length);

    // ── VALIDATOR CONTEXT ────────────────────────────────────────────────
    // validateRota now lives in rota.js. Gather the closure variables it
    // needs into an explicit ctx object — preserves admin's prompt-time data
    // capture without the nested-function pattern.
    const rotaCtx = {
      templates: templates,
      firstNames: firstNames,
      staff: storeStaff,
      doByStaffId: doByStaffId,
      deliveryDayStarts: mergedDeliveryStarts,
      hasManagerNotes: hasManagerNotes,
      historyByStaffId: historySummary ? historySummary.byStaffId : null,   // v131.6 rule 9
      hoursByDay: coverHours,   // v132.100: the store's own closing times, when set up
      awayByStaffId: away,      // v133.98
      onSiteMins: ramadan ? 420 : 540,   // v133.99: a full shift's length (6h + lunch in Ramadan), for rule 5
    };

    // ── AI call helper ──────────────────────────────────────────────────
    async function callAi(p) {
      // v131.8 (R4b) - the model is FORCED onto the rota tool, so the API
      // validates the output shape before we see it. parseRotaResponse still
      // falls back to the old text path if a model ignores the tool.
      const resp = await withTimeout(DB.ai.rota({
        purpose: 'rota-generate',   // v131.7 - env ROTA_MODEL swaps this with no deploy
        model: 'claude-sonnet-4-6',
        max_tokens: 4000,
        system: 'You are a retail rota planner. Submit the rota by calling the rota tool.',
        messages: [{ role: 'user', content: p }],
        tools: [rotaOutputTool()],
        tool_choice: { type: 'tool', name: 'rota' }
      }), AI_CALL_LIMIT_MS, TOO_SLOW);   // v133.94
      const parsed = parseRotaResponse(resp);
      if (!parsed) throw new Error('Empty response from AI');
      return parsed;
    }

    // ── Generate: first attempt + retry-on-critical ─────────────────────
    const firstAt = ticker.secs();
    let parsed = await callAi(prompt);
    let workShifts = normaliseShiftLunches((parsed.shifts || []).filter(s => s.type !== 'off' && s.start));
    let v = validateRota(workShifts, rotaCtx);
    // v133.94: a slow first call keeps its draft (its issues show as warnings) rather than doubling the wait
    if (v.critical.length > 0 && ticker.secs() - firstAt < AI_SKIP_REFINE_SECS) {
      ticker.setLabel('Refining...');
      const retryPrompt = prompt + '\n\nYOUR PREVIOUS ATTEMPT HAD PROBLEMS:\n' +
        v.critical.map(c => '- ' + c).join('\n') + '\nPlease try again and fix these specific issues. Return only JSON.';
      try {
        const parsed2 = await callAi(retryPrompt);
        const workShifts2 = normaliseShiftLunches((parsed2.shifts || []).filter(s => s.type !== 'off' && s.start));
        const v2 = validateRota(workShifts2, rotaCtx);
        if (v2.critical.length < v.critical.length) { parsed = parsed2; workShifts = workShifts2; v = v2; }
      } catch(retryErr) { console.warn('[AI rota] retry failed:', retryErr.message); }
    }

    // ── Auto-fill missing shifts ────────────────────────────────────────
    // v133.98: first take out any shift the model still put on an away day (said so below)
    const awayDrop = ROTA.dropAwayShifts(workShifts, storeStaff, away);
    workShifts = awayDrop.kept;
    const autoFills = awayDrop.dropped.map(d => 'Removed a shift for ' + d + ': not here that day');
    firstNames.forEach(name => {
      const sMatch = storeStaff.find(x => x.name.split(' ')[0] === name);
      if (!sMatch) return;
      const off = doByStaffId[sMatch.id];
      DAYS.forEach(day => {
        if (day === off) return;
        if (ROTA.isAwayDay(away, sMatch.id, day)) return;   // v133.98: never fill over leave or another store
        if (workShifts.some(s => s.staffName === name && s.day === day)) return;
        const arrival = mergedDeliveryStarts[day]   // v131.5 - the TYPED delivery counts here too;
        // v131.4 — covered = somebody ON SITE at arrival (a spanning shift).
        const deliveryUncovered = arrival && !workShifts.some(s => s.day === day && s.start && s.finish &&
          timeToMin(s.start) <= timeToMin(arrival) && timeToMin(s.finish) >= timeToMin(arrival));
        let filled;
        if (deliveryUncovered && deliveryModeForDay(templates, day, arrival, coverHours) === 'early') {
          const arrivalMin = timeToMin(arrival);
          const finishMin = Math.min(arrivalMin + 540, timeToMin(ROTA.closeFor(day, coverHours)));
          filled = {
            staffName: name, day: day, start: arrival,
            finish: pad(Math.floor(finishMin/60)) + ':' + pad(finishMin%60),
            lunchAt: lunchForShift(arrival, pad(Math.floor(finishMin/60)) + ':' + pad(finishMin%60)).lunchAt,
            lunch:   lunchForShift(arrival, pad(Math.floor(finishMin/60)) + ':' + pad(finishMin%60)).lunch,
          };
        } else {
          const allowed = allowedTemplatesForDay(templates, day, coverHours);
          // v131.4 — on a span-mode delivery day, prefer a template covering arrival.
          const tpl = (arrival ? allowed.filter(t =>
            timeToMin(t.start) <= timeToMin(arrival) && timeToMin(t.finish) >= timeToMin(arrival))[0] : null)
            || allowed[0] || templates[0];
          if (!tpl) return;
          filled = {
            staffName: name, day: day, start: tpl.start, finish: tpl.finish,
            lunchAt: computeLunchAt(tpl.start), lunch: 60,
          };
        }
        workShifts.push(filled);
        autoFills.push(name + ' ' + day + ': auto-filled ' + filled.start + '–' + filled.finish + (deliveryUncovered ? ' (delivery cover)' : ' (AI missed this day)'));
      });
    });
    // (Re-validated once, below, AFTER lunch optimisation - so the warnings
    // describe the rota on screen, not the pre-optimisation draft.)

    // ── Lunch coverage optimisation ─────────────────────────────────────
    DAYS.forEach(day => {
      const ds = workShifts.filter(s => s.day === day);
      optimiseLunchesForDay(ds, cover ? needAtFn(cover, day) : undefined);   // v132.100: quiet hours first
    });

    // ── FINAL VALIDATION — of what will actually be shown (see staff app) ──
    v = validateRota(workShifts, rotaCtx);

    // ── Merge D/Os + stamp type:work ────────────────────────────────────
    const workShiftsTyped = workShifts.map(s => Object.assign({}, s, { type: 'work' }));
    const allShifts = doEntries.concat(ROTA.awayEntries(storeStaff, away, doByStaffId), workShiftsTyped);   // v133.98: 'away' is draft-only

    // ── Sanity + warnings ───────────────────────────────────────────────
    if (workShifts.length === 0) throw new Error('AI produced no work shifts — please try again');

    const allWarnings = [];
    v.critical.forEach(c => allWarnings.push('❗ ' + c));
    v.advisory.forEach(a => allWarnings.push('⚠️ ' + a));
    autoFills.forEach(a => allWarnings.push('ℹ️ ' + a));
    // v132.100: the cover check on the draft (advice only; never blocks publish).
    if (cover) { try { coverWarnings(workShifts, cover).forEach(w => allWarnings.push('🕐 Cover: ' + w)); } catch (e) { /* advice only */ } }
    if (parsed.warnings && Array.isArray(parsed.warnings)) parsed.warnings.forEach(w => allWarnings.push(w));

    ticker.stop();
    btn.disabled = false;
    const warnNote = v.critical.length > 0
      ? ' — ' + v.critical.length + ' issue(s) remain, review carefully'
      : (allWarnings.length ? ' — ' + allWarnings.length + ' note(s)' : '');
    status.innerHTML = '✅ Rota generated' + warnNote + ' — review and publish below';
    if (intakeApplied && intakeApplied.understood.length) {
      const eh = (typeof escapeHtml === 'function') ? escapeHtml : (x => String(x));
      status.innerHTML += '<div style="margin-top:8px;font-size:12px;text-align:start"><strong>Understood from your notes:</strong><br>' +
        intakeApplied.understood.map(u => '&bull; ' + eh(u)).join('<br>') + '</div>';
    }
    result.style.display = 'block';

    window._pendingRota = { shifts: allShifts, store, week, storeStaff };

    // ── Render result card ──────────────────────────────────────────────
    const warningsHtml = allWarnings.map(w => '<div class="ai-warning">' + w + '</div>').join('');
    // v131.10 - the Understood summary belongs with the draft, not the
    // transient status line (see the staff app).
    const ehU = (typeof escapeHtml === 'function') ? escapeHtml : (x => String(x));
    const understoodHtml = (intakeApplied && intakeApplied.understood.length)
      ? '<div style="background:var(--adm-ok-pale);border:1px solid var(--adm-ok-line);border-radius:10px;padding:10px 12px;font-size:12.5px;margin-bottom:10px"><strong>Understood from your notes:</strong><br>' +
        intakeApplied.understood.map(u => '&bull; ' + ehU(u)).join('<br>') + '</div>'
      : '';
    const noteHtml = parsed.notes ? '<div class="ai-notes">💡 ' + escapeHtml(parsed.notes) + '</div>' : '';
    const displayDays = weekOrder;
    const countryBadge = isQatar
      ? '<span style="background:var(--info-bg);color:var(--adm-sky-text);padding:2px 8px;border-radius:6px;font-size:11px;font-weight:600;margin-inline-start:8px">🇶🇦 Qatar — Sun–Sat week</span>'
      : '<span style="background:var(--adm-ok-pale);color:var(--ok-deep);padding:2px 8px;border-radius:6px;font-size:11px;font-weight:600;margin-inline-start:8px">🇦🇪 UAE — Mon–Sun week</span>';
    let grid = '<div style="overflow-x:auto"><table class="draft-table"><thead><tr><th>Staff</th>' + displayDays.map(d => '<th>' + d + '</th>').join('') + '</tr></thead><tbody>';
    storeStaff.forEach(s => {
      const firstName = s.name.split(' ')[0];
      grid += '<tr><td translate="no">' + firstName + '</td>';
      displayDays.forEach(day => {
        const sh = allShifts.find(x => (x.staffName === firstName || x.staffName === s.name || s.name.startsWith(x.staffName) || firstName.startsWith(x.staffName)) && x.day === day);
        if (!sh) {
          grid += '<td><div class="draft-off" style="background:var(--adm-danger-pale);color:var(--adm-red);border:1px solid var(--adm-danger-line)">? NO SHIFT</div></td>';
        } else if (sh.type === 'away') {   // v133.98: leave or another store
          grid += '<td><div class="draft-off" style="background:var(--info-bg);color:var(--info-deep);border:1px solid var(--adm-info-line)" title="' + escapeHtml(sh.reason || '') + '">' + (sh.reason === 'on holiday' ? 'Hol' : 'Away') + '</div></td>';
        } else if (sh.type && sh.type.toLowerCase().indexOf('off') > -1) {
          grid += '<td><div class="draft-off">D/O</div></td>';
        } else if (!sh.start || !sh.finish) {
          grid += '<td><div class="draft-off" style="background:var(--adm-danger-pale);color:var(--adm-red);border:1px solid var(--adm-danger-line)">? NO TIMES</div></td>';
        } else {
          grid += '<td><div class="draft-shift">' + sh.start + '–' + sh.finish + '</div></td>';
        }
      });
      grid += '</tr>';
    });
    grid += '</tbody></table></div>';

    result.innerHTML =
      '<div class="ai-result-card">' +
      '  <div class="ai-result-header">' +
      '    <div><div class="ai-result-title">Proposed Rota — ' + store + countryBadge + '</div>' +
      '    <div class="ai-result-sub">Week of ' + week + ' · ' + storeStaff.length + ' staff</div></div>' +
      '    <span class="badge badge-amber" id="rotaDraftBadge">DRAFT</span>' +
      '  </div>' +
      understoodHtml + (warningsHtml ? '<div class="ai-warnings">' + warningsHtml + '</div>' : '') +
      noteHtml + grid +
      '  <button class="publish-btn" id="publishRotaBtn" onclick="Uploads.rota.publishRota()">Publish This Rota</button>' +
      '</div>';

  } catch(err) {
    ticker.stop();
    btn.disabled = false;
    status.innerHTML = '❌ ' + (err.message || 'Something went wrong');
    showToast('Generation failed: ' + (err.message || 'unknown error'), 'error');
  }
}

export async function publishRota() {
  const rota = window._pendingRota;
  if (!rota || !rota.shifts || !rota.shifts.length) {
    showToast('No rota to publish — generate one first', 'error');
    return;
  }

  const btn = document.getElementById('publishRotaBtn');
  btn.disabled = true;
  btn.textContent = 'Publishing…';

  const { shifts, store, week, storeStaff } = rota;

  // Map AI shift format → DB schema
  // AI returns: {staffName, day, type, start, finish, lunchAt, lunch}
  // DB expects: {staff_id, week_start, day, type, start_time, end_time, lunch_at, lunch_mins}
  const dbRows = [];
  const unmatchedNames = [];

  shifts.forEach(sh => {
    if (sh.type === 'away') return;   // v133.98: draft-only; the saved rota already holds that day
    // Match staffName to a staff_id
    const match = storeStaff.find(s =>
      s.name === sh.staffName ||
      s.name.split(' ')[0] === sh.staffName ||
      s.name.startsWith(sh.staffName)
    );
    if (!match) {
      if (unmatchedNames.indexOf(sh.staffName) === -1) unmatchedNames.push(sh.staffName);
      return;
    }

    const shiftType = (sh.type || 'work').toLowerCase();
    dbRows.push({
      staff_id: match.id,
      week_start: week,
      day: sh.day,
      type: shiftType === 'off' ? 'off' : 'work',
      start_time: shiftType === 'off' ? null : (sh.start || null),
      end_time: shiftType === 'off' ? null : (sh.finish || null),
      lunch_at: shiftType === 'off' ? null : (sh.lunchAt || null),
      lunch_mins: shiftType === 'off' ? 0 : (sh.lunch || 60),
      // v129.68: explicit null clears any stale variance-TOIL flag on the
      // overwritten row (merge-duplicates preserves absent columns).
      variance_toil: null,
      // v57: admin publishes are attributed to the admin user
      edited_by_staff_id: null, // admins aren't in the staff table
      edited_by_staff_name: (currentAdmin && currentAdmin.name) ? currentAdmin.name + ' (admin)' : 'Admin',
    });
  });

  // v136.12 (19 Oct 2026): one row per person and day. The draft can hold two lines for the same person and day
  // (a shift on the day off it was given, a day listed twice, or two first names matching the same person), and two
  // rows with the same key made the server refuse the WHOLE publish. The later line wins, as the server would.
  const ddPub = dedupeShiftRows(dbRows);
  if (ddPub.merged) {
    console.warn('[rota] publish: ' + ddPub.merged + ' duplicate day(s) in the draft merged (the later line kept)');
    dbRows.length = 0;
    ddPub.rows.forEach(r => dbRows.push(r));
  }

  if (unmatchedNames.length) {
    showToast('Warning: could not match ' + unmatchedNames.join(', ') + ' to staff records', 'error');
  }

  if (!dbRows.length) {
    showToast('No shifts could be mapped to staff — check names', 'error');
    btn.disabled = false;
    btn.textContent = 'Publish This Rota';
    return;
  }

  try {
    // v133.95: the AI publish keeps booked leave, as the staff app's has since v129.84. Without the flag
    // db.js sends allow_leave_overwrite, so an AI draft that put someone on a holiday day replaced the
    // holiday with a shift (pay-affecting). The Worker now keeps those days and the save toasts them.
    await DB.shifts.save(dbRows, { guardLeave: true });
    showToast('Rota published — ' + dbRows.length + ' shifts saved ✓', 'success');
    btn.textContent = '✅ Published';
    btn.style.background = 'var(--gl)';
    btn.onclick = null;
    // Update badge
    const badge = document.getElementById('rotaDraftBadge');
    if (badge) { badge.textContent = 'PUBLISHED'; badge.className = 'badge badge-green'; }
    window._pendingRota = null;
  } catch(e) {
    console.error('[DB] Rota publish failed:', e);
    showToast('Publish failed: ' + (e.message || 'unknown error'), 'error');
    btn.disabled = false;
    btn.textContent = 'Publish This Rota';
  }
}

export async function auditShifts(week, store) {
  try {
    const params = {};
    if (week) params.week = week;
    if (store) params.store = store;
    const rows = await DB.shifts.listAdmin(params);
    const edited = (rows || []).filter(r => r.edited_by_staff_name);
    const table = edited.map(r => ({
      week: r.week_start,
      day: r.day,
      staff_id: r.staff_id,
      store: r.work_store || '',
      type: r.type,
      shift: (r.start_time || '-') + '-' + (r.end_time || '-'),
      edited_by: r.edited_by_staff_name,
      edited_by_id: r.edited_by_staff_id,
    }));
    console.log('── Shift edits (with attribution): ' + table.length + ' of ' + (rows || []).length + ' rows ──');
    console.table(table);
    const unattributed = (rows || []).length - table.length;
    if (unattributed > 0) console.log('(' + unattributed + ' rows with no attribution — either pre-v57 data or direct DB edits)');
    return table;
  } catch(e) {
    console.error('[auditShifts] failed:', e.message);
    return [];
  }
}

export async function auditDeliveries(store) {
  try {
    const rows = await DB.delivery.list(store);
    const edited = (rows || []).filter(r => r.edited_by_staff_name);
    const table = edited.map(r => ({
      store: r.store,
      day: r.day_of_week,
      window: (r.window_start || '-') + '-' + (r.window_end || '-'),
      notes: r.notes || '',
      edited_by: r.edited_by_staff_name,
      edited_by_id: r.edited_by_staff_id,
    }));
    console.log('── Delivery edits (with attribution): ' + table.length + ' of ' + (rows || []).length + ' rows ──');
    console.table(table);
    return table;
  } catch(e) {
    console.error('[auditDeliveries] failed:', e.message);
    return [];
  }
}

